import { AddQuotaButton, CheckboxesToArrayField, ErrorMsg } from 'components';
import React, { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { DevicesCategory, RequiredMediaSource } from '@app/api';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { QuotaInputList } from 'pages/Dashboard/EditSession/components/Quota/QuotaInputList';
import { useOnClickAddQuotas } from 'pages/Dashboard/EditSession/components/Quota/useQuotasHooks';
import { NewQuota } from '@app/api';
import { useTranslation } from 'react-i18next';

export const DEVICE_TYPE_ICONS = {
  COMPUTER: 'ri-computer-line',
  TABLET: 'ri-tablet-line',
  SMARTPHONE: 'ri-smartphone-line',
};

const REQUIRED_MEDIA_ICONS = {
  VIDEO: 'ri-webcam-line',
  AUDIO: 'ri-mic-line',
  SCREEN_SHARING: 'ri-cast-line',
};

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
  },
  checkbox: {
    minWidth: 'unset',
  },
  errorMessageContainer: {
    height: theme.marginBase * 4,
  },
  quotaContainer: {
    border: `1px solid ${theme.colors.grayBorder}`,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.std,
  },
}));

interface Props {
  displayMediaSource?: boolean;
  numberOfParticipantRequired?: number;
}

export const SessionTechnicalNeeds = ({
  displayMediaSource,
  numberOfParticipantRequired = 0,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();

  const REQUIRED_MEDIA_SOURCE_OPTIONS = Object.values(RequiredMediaSource).map(
    (need: RequiredMediaSource) => {
      return {
        label: t(`sessions.display.requiredMediaSourceChoices.${need}`),
        value: need,
        icon: REQUIRED_MEDIA_ICONS[need],
      };
    },
  );

  const DEVICES_CATEGORY_OPTIONS = Object.values(DevicesCategory).map(
    (device: DevicesCategory) => {
      return {
        label: t(`sessions.display.allowedDevicesCategoriesChoices.${device}`),
        value: device,
        icon: DEVICE_TYPE_ICONS[device],
      };
    },
  );

  const allowedDevicesCategories = useMemo(
    () => values.allowedDevicesCategories || [],
    [values.allowedDevicesCategories],
  );

  const allowedDevicesCategoriesQuotas = useMemo(
    () => values.allowedDevicesCategoriesQuotas || [],
    [values.allowedDevicesCategoriesQuotas],
  );

  const showQuotas =
    !!allowedDevicesCategoriesQuotas &&
    allowedDevicesCategoriesQuotas.length > 0;

  const handleQuotasButton = useOnClickAddQuotas({
    hasQuotas: showQuotas,
    selected: allowedDevicesCategories,
    allOptions: DevicesCategory,
    setFieldValue: setFieldValue,
    numberOfParticipantRequired,
    categoriesFieldName: 'allowedDevicesCategories',
    quotasFieldName: 'allowedDevicesCategoriesQuotas',
  });

  const handleQuotasChange = useCallback(
    (quotas: NewQuota[]) => {
      setFieldValue('allowedDevicesCategoriesQuotas', quotas);
    },
    [setFieldValue],
  );

  const handleAllowedDevicesChange = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const { checked, value } = e.currentTarget;

      const current = values.allowedDevicesCategories || [];
      const currentQuotas = allowedDevicesCategoriesQuotas || [];

      const newSelected = checked
        ? [...current, value]
        : current.filter((v: string) => v !== value);

      setFieldValue('allowedDevicesCategories', newSelected);

      if (currentQuotas.length > 0) {
        const updatedQuotas = newSelected.map((device: string) => {
          const existing = currentQuotas.find(
            (quota: NewQuota) => quota.item === device,
          );
          return existing ?? { item: device, max: 0 };
        });
        setFieldValue('allowedDevicesCategoriesQuotas', updatedQuotas);
      }
    },
    [
      values.allowedDevicesCategories,
      allowedDevicesCategoriesQuotas,
      setFieldValue,
    ],
  );

  return (
    <>
      {displayMediaSource && (
        <CheckboxesToArrayField
          name="requiredMediaSource"
          label={t('sessions.display.requiredMediaSourceLabel')}
          options={REQUIRED_MEDIA_SOURCE_OPTIONS}
          checkboxClassName={classes.checkbox}
        />
      )}
      <CheckboxesToArrayField
        name="allowedDevicesCategories"
        label={t('sessions.display.allowedDevicesLabel')}
        options={DEVICES_CATEGORY_OPTIONS}
        checkboxClassName={classes.checkbox}
        noMargin={displayMediaSource}
        onChange={handleAllowedDevicesChange}
        rightAction={
          <AddQuotaButton value={showQuotas} onClick={handleQuotasButton} />
        }
      />
      {showQuotas && allowedDevicesCategories.length > 0 && (
        <div className={classes.quotaContainer}>
          <QuotaInputList
            quotas={allowedDevicesCategoriesQuotas}
            options={DEVICES_CATEGORY_OPTIONS}
            numberOfParticipantRequired={numberOfParticipantRequired}
            onChange={handleQuotasChange}
          />
        </div>
      )}
      <div className={classes.errorMessageContainer}>
        <ErrorMsg name="allowedDevicesCategories" />
      </div>
    </>
  );
};
